<template>
    <div class="notification-box" :class="{'in-toast': inToast, 'read': message.read}">
        <div class="notification-header">
            <div :class="`status-box ${message.message_type}`">
                {{ message.message_type }}
            </div>
            <div class="timeago-text" v-if="inToast">Just now</div>
            <div class="timeago-text" v-else-if="message.created_at"><timeago :datetime="message.created_at"/></div>
        </div>
        <div class="line"></div>
        <div>
            <div class="notification-content" >
                <div class="break-line-or-txt" v-for="(m, key) in processedMessage" :key="key">{{ m }}</div>
                <span v-if="message.more_info_url && message.more_info_text">
                    <br/>
                    <a href="#" :onclick="`safeRedirect('${message.more_info_url}')`">{{ message.more_info_text || 'View details' }}</a>
                </span>
            </div>
            <div v-if="!inToast" class="view-details" @click="openNotification">
                <div class="view-text">View details</div>
                <img src="/assets/img/view.svg"/>
            </div>
        </div>
        <div class="close" @click="deleteNotification">
            <img src="/assets/img/close.svg">
        </div>
    </div>
</template>
<script lang="ts">
declare global {
  interface Window { 
    safeRedirect: any; 
  }
}



import { useAPIStore } from '@/stores/APIStore';
import { safeRedirect } from '@/utils/helpers';
import { computed, toRefs } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

/**
 * A single notification. To be used in the notification sidebar, or in a toast
 */
export default {
    props: {
        /**
         * The message of the notification
         */
        message: {
            type: Object,
        },
        /**
         * Whether or not this notification is being used as part of the alert toasts
         */
        inToast: {
            type: Boolean,
        }
    },
    setup(props, {emit}){
        const apiStore = useAPIStore()
        const router = useRouter()

        const { inToast, message } = toRefs(props)


        const processedMessage = computed(() => {
            const newMsg = message?.value?.message ? message.value.message.split("\\n") : []
            return newMsg
        })

        /**
         * This regex filters urls from plain text messages and returns text with interactable anchor tags
         * @param {string} message the messages to be processed
         */
        const processUrls = (message : string) => {
            var text = message
            var exp = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig
            var text1 = text.replace(exp, "<a href='$1'>$1</a>")
            var exp2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim
            return text1.replace(exp2, '$1<a target="_blank" href="http://$2">$2</a>')
        }

        /**
         * Deletes this notification
         */
        const deleteNotification = () => {
            if(!inToast.value){
                apiStore.deleteNotificationByID(props.message.id)
            }
        }

        /**
         * Redirects to the notification detail page
         */
        const openNotification = () => {
            emit('closePopup')
            router.push(`/notification/${props.message.id}`)
        }

        window.safeRedirect = safeRedirect

        

        return {
            processUrls,
            deleteNotification,
            openNotification,
            processedMessage,
            safeRedirect
        }
    }
}
</script>
<style lang="scss">
.notification-box{
    position: relative;
    width: 100%;
    border-radius: 8px;
    border: 1px solid var(--Neutral-Gray-300, #CED2DA);
    background: var(--Wit, #FFF);
    box-shadow: var(--drop-shadow-on-light);
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 24px;

    &.read{
        opacity: 0.4;
    }

    &.in-toast{
        border-radius: 8px 8px 0px 0px;
    }

    .notification-header{
        display: flex;
        color: var(--Neutral-Gray-400);
        justify-content: space-between;
        align-items: center;
    }

    .status-box{
        padding: 4px 12px;
        border-radius: 19px;
        border: 1px solid var(--Primary); 
        color: var(--Primary-500);
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        width: fit-content;
        text-transform: capitalize;

        &.success{
            border: 1px solid var(--Success-500); 
            color: var(--Success-500);
        }

        &.error{
            border: 1px solid var(--Error-500); 
            color: var(--Error-500);
        }

        &.warning{
            border: 1px solid var(--Warning-500); 
            color: var(--Warning-500);
        }
    }

    .timeago-text{
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;
    }

    .line{
        width: 100%;
        height: 1px;
        background-color: var(--Neutral-Gray-200);
    }

    .notification-content{
        color: var(--Black);
        word-wrap: break-word;
        margin-bottom: 20px;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        white-space: break-spaces;
    }

    .view-details{
        position: absolute;
        display: flex;
        gap: 4px;
        color: var(--Neutral-Gray-400);
        cursor: pointer;
        margin-top: 4px;
        bottom: 15px;
        right: -20px;
        width: 120px;
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;

        &:hover{
            color: var(--Primary);
        }
    }

    .close{
        position: absolute;
        top: -5px;
        right: -5px;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background-color: var(--Neutral-Gray-400);
        opacity: 1;
        cursor: pointer;
        transition: 0.2s;
        opacity: 0 !important;

        &:hover{
            background-color: var(--Error-500);
            opacity: 0 !important;
        }
        
        img{
            position: absolute;
            top: 3px;
            left: 3px;
            width: 8px;
            height: 8px;
        }
    }

    &:hover{
        .close{
            opacity: 1 !important;

            &:hover{
                opacity: 1 !important;
            }
        }
    }
}
</style>